(() => {
    'use strict';

    const runtimeRoot =
        document.documentElement;

    if (
        runtimeRoot.dataset
            .mainProductRuntimeReady ===
        'true'
    ) {
        return;
    }

    runtimeRoot.dataset
        .mainProductRuntimeReady =
        'true';

    const strings = {
        unavailable:
            'This combination is unavailable.',

        addedToCart:
            'Added to cart',

        cartError:
            'Something went wrong. Please try again.'
    };

    const dispatchDocumentEvent = (
        name,
        detail,
        legacyName = ''
    ) => {
        document.dispatchEvent(
            new CustomEvent(
                name,
                {
                    bubbles: true,
                    detail
                }
            )
        );

        /*
         * Keep the current cart-drawer compatibility path while the
         * storefront finishes moving to the neutral theme:* event names.
         */
        if (legacyName) {
            document.dispatchEvent(
                new CustomEvent(
                    legacyName,
                    {
                        bubbles: true,
                        detail
                    }
                )
            );
        }
    };

    /* ==========================================================
       Product Gallery
       ========================================================== */

    class ProductGallery {
        constructor(section) {
            this.section = section;

            this.gallery =
                section.querySelector(
                    '[data-product-gallery]'
                );

            if (!this.gallery) {
                return;
            }

            this.thumbnails = [
                ...this.gallery.querySelectorAll(
                    '[data-gallery-thumbnail]'
                )
            ];

            this.mediaItems = [
                ...this.gallery.querySelectorAll(
                    '[data-gallery-media]'
                )
            ];

            this.status =
                this.gallery.querySelector(
                    '[data-gallery-status]'
                );

            this.activeIndex =
                this.getInitialIndex();

            /* ------------------------------------------------------
               Touch state
               ------------------------------------------------------ */

            this.touching = false;
            this.touchMoved = false;

            this.touchStartX = 0;
            this.touchStartY = 0;

            this.touchCurrentX = 0;
            this.touchCurrentY = 0;

            this.gesture = 'idle';

            /* ------------------------------------------------------
               Pinch state
               ------------------------------------------------------ */

            this.pinchStartDistance = 0;
            this.pinchStartZoom = 1;

            this.pinchStartPanX = 0;
            this.pinchStartPanY = 0;

            /* ------------------------------------------------------
               Pan state
               ------------------------------------------------------ */

            this.panStartTranslateX = 0;
            this.panStartTranslateY = 0;

            /* ------------------------------------------------------
               Zoom
               ------------------------------------------------------ */

            this.zoomTarget = null;
            this.zoomed = false;

            this.zoomScale = 1;
            this.zoomMinScale = 1;
            this.zoomMaxScale = 2.25;

            this.zoomPanX = 0;
            this.zoomPanY = 0;

            this.desktopZoomScale = 1.75;

            /* ------------------------------------------------------
               Environment
               ------------------------------------------------------ */

            this.isReducedMotion =
                window.matchMedia(
                    '(prefers-reduced-motion: reduce)'
                ).matches;

            this.supportsHoverZoom =
                window.matchMedia(
                    '(hover: hover) and (pointer: fine)'
                ).matches;

            /* ------------------------------------------------------
               Bound handlers
               ------------------------------------------------------ */

            this.handleClick =
                this.handleClick.bind(this);

            this.handleKeydown =
                this.handleKeydown.bind(this);

            this.handleTouchStart =
                this.handleTouchStart.bind(this);

            this.handleTouchMove =
                this.handleTouchMove.bind(this);

            this.handleTouchEnd =
                this.handleTouchEnd.bind(this);

            this.handlePointerMove =
                this.handlePointerMove.bind(this);

            this.handlePointerLeave =
                this.handlePointerLeave.bind(this);
        }

        /* ==========================================================
           Init
           ========================================================== */

        init() {
            if (
                !this.gallery ||
                this.gallery.dataset.initialized ===
                'true'
            ) {
                return;
            }

            this.gallery.dataset.initialized =
                'true';

            this.gallery.addEventListener(
                'click',
                this.handleClick
            );

            this.gallery.addEventListener(
                'keydown',
                this.handleKeydown
            );

            this.gallery.addEventListener(
                'touchstart',
                this.handleTouchStart,
                {
                    passive: true,
                    capture: true
                }
            );

            this.gallery.addEventListener(
                'touchmove',
                this.handleTouchMove,
                {
                    passive: true,
                    capture: true
                }
            );

            this.gallery.addEventListener(
                'touchend',
                this.handleTouchEnd,
                {
                    passive: true,
                    capture: true
                }
            );

            if (this.supportsHoverZoom) {
                this.gallery.addEventListener(
                    'pointermove',
                    this.handlePointerMove
                );

                this.gallery.addEventListener(
                    'pointerleave',
                    this.handlePointerLeave
                );
            }

            this.update(
                this.activeIndex,
                {
                    initial: true
                }
            );
        }

        /* ==========================================================
           Initial index
           ========================================================== */

        getInitialIndex() {
            const thumbnailIndex =
                this.thumbnails.findIndex(
                    (thumbnail) =>
                        thumbnail.getAttribute(
                            'aria-current'
                        ) === 'true' ||
                        thumbnail.classList.contains(
                            'is-active'
                        )
                );

            if (thumbnailIndex >= 0) {
                return thumbnailIndex;
            }

            const mediaIndex =
                this.mediaItems.findIndex(
                    (media) =>
                        media.classList.contains(
                            'is-active'
                        )
                );

            return mediaIndex >= 0
                ? mediaIndex
                : 0;
        }

        /* ==========================================================
           Click
           ========================================================== */

        handleClick(event) {
            const thumbnail =
                event.target.closest(
                    '[data-gallery-thumbnail]'
                );

            if (
                thumbnail &&
                this.gallery.contains(thumbnail)
            ) {
                const index =
                    Number(
                        thumbnail.dataset.galleryIndex
                    );

                if (Number.isInteger(index)) {
                    this.update(index);
                }

                return;
            }

            const zoomTarget =
                event.target.closest(
                    '[data-gallery-zoom]'
                );

            if (!zoomTarget) {
                return;
            }

            const media =
                zoomTarget.closest(
                    '[data-gallery-media]'
                );

            if (
                !media ||
                media.dataset.mediaType !==
                'image' ||
                !media.classList.contains(
                    'is-active'
                )
            ) {
                return;
            }

            /* Desktop: click to zoom. */

            if (this.supportsHoverZoom) {
                if (this.zoomed) {
                    this.resetZoom();
                } else {
                    this.activateDesktopZoom(
                        zoomTarget,
                        event
                    );
                }

                return;
            }

            /*
             * Mobile:
             * don't turn a real gesture into a
             * synthetic tap-to-zoom.
             */

            if (
                this.touchMoved ||
                this.gesture === 'swipe' ||
                this.gesture === 'pan' ||
                this.gesture === 'pinch' ||
                this.gesture ===
                'vertical-scroll'
            ) {
                return;
            }

            this.toggleMobileZoom(
                zoomTarget
            );
        }

        /* ==========================================================
           Keyboard
           ========================================================== */

        handleKeydown(event) {
            if (
                !this.gallery?.contains(
                    event.target
                )
            ) {
                return;
            }

            if (
                event.target.matches(
                    'input, select, textarea'
                )
            ) {
                return;
            }

            if (
                event.key === 'Escape' &&
                this.zoomed
            ) {
                event.preventDefault();

                this.resetZoom();

                return;
            }

            if (
                event.target.matches(
                    '[data-gallery-zoom]'
                ) &&
                (
                    event.key === 'Enter' ||
                    event.key === ' '
                )
            ) {
                event.preventDefault();

                if (this.supportsHoverZoom) {
                    if (this.zoomed) {
                        this.resetZoom();
                    } else {
                        this.activateDesktopZoom(
                            event.target
                        );
                    }
                } else {
                    this.toggleMobileZoom(
                        event.target
                    );
                }

                return;
            }

            if (event.key === 'ArrowLeft') {
                event.preventDefault();

                this.previous();

                return;
            }

            if (event.key === 'ArrowRight') {
                event.preventDefault();

                this.next();

                return;
            }

            if (event.key === 'Home') {
                event.preventDefault();

                this.update(
                    0,
                    {
                        focus: true
                    }
                );

                return;
            }

            if (event.key === 'End') {
                event.preventDefault();

                this.update(
                    this.mediaItems.length - 1,
                    {
                        focus: true
                    }
                );
            }
        }

        /* ==========================================================
           Touch helpers
           ========================================================== */

        getTouchDistance(
            touchA,
            touchB
        ) {
            return Math.hypot(
                touchB.clientX -
                touchA.clientX,
                touchB.clientY -
                touchA.clientY
            );
        }

        getActiveZoomTarget() {
            const media =
                this.mediaItems[
                this.activeIndex
                ];

            if (
                !media ||
                media.dataset.mediaType !==
                'image' ||
                !media.classList.contains(
                    'is-active'
                )
            ) {
                return null;
            }

            return media.querySelector(
                '[data-gallery-zoom]'
            );
        }

        /* ==========================================================
           Touch start
           ========================================================== */

        handleTouchStart(event) {
            if (
                !event.target.closest(
                    '.product__gallery-viewer'
                )
            ) {
                return;
            }

            /* Two-finger pinch. */

            if (
                event.touches.length === 2
            ) {
                const zoomTarget =
                    this.getActiveZoomTarget();

                if (!zoomTarget) {
                    return;
                }

                const touchA =
                    event.touches[0];

                const touchB =
                    event.touches[1];

                this.zoomTarget =
                    zoomTarget;

                if (!this.zoomed) {
                    this.zoomed = true;

                    this.zoomScale = 1;

                    this.zoomPanX = 0;
                    this.zoomPanY = 0;

                    zoomTarget.classList.add(
                        'is-zoomed'
                    );

                    zoomTarget.setAttribute(
                        'aria-expanded',
                        'true'
                    );

                    this.setZoomScale(
                        zoomTarget,
                        1
                    );

                    this.setZoomPan(
                        zoomTarget,
                        0,
                        0
                    );

                    this.applyMobileZoom(
                        zoomTarget
                    );
                }

                this.gesture =
                    'pinch';

                this.touching = true;
                this.touchMoved = false;

                this.pinchStartDistance =
                    Math.max(
                        1,
                        this.getTouchDistance(
                            touchA,
                            touchB
                        )
                    );

                this.pinchStartZoom =
                    this.zoomScale;

                this.pinchStartPanX =
                    this.zoomPanX;

                this.pinchStartPanY =
                    this.zoomPanY;

                return;
            }

            if (
                event.touches.length !== 1
            ) {
                return;
            }

            const touch =
                event.touches[0];

            this.touchStartX =
                touch.clientX;

            this.touchStartY =
                touch.clientY;

            this.touchCurrentX =
                touch.clientX;

            this.touchCurrentY =
                touch.clientY;

            this.touching = true;
            this.touchMoved = false;

            if (
                !this.supportsHoverZoom &&
                this.zoomed &&
                this.zoomTarget
            ) {
                this.gesture =
                    'possible-pan';

                this.panStartTranslateX =
                    this.zoomPanX;

                this.panStartTranslateY =
                    this.zoomPanY;

                return;
            }

            this.gesture =
                'possible-swipe';
        }

        /* ==========================================================
           Touch move
           ========================================================== */

        handleTouchMove(event) {
            if (!this.touching) {
                return;
            }

            /* Pinch. */

            if (
                event.touches.length === 2 &&
                this.zoomTarget
            ) {
                const touchA =
                    event.touches[0];

                const touchB =
                    event.touches[1];

                const distance =
                    Math.max(
                        1,
                        this.getTouchDistance(
                            touchA,
                            touchB
                        )
                    );

                const ratio =
                    distance /
                    this.pinchStartDistance;

                this.zoomScale =
                    this.clamp(
                        this.pinchStartZoom *
                        ratio,
                        this.zoomMinScale,
                        this.zoomMaxScale
                    );

                this.zoomPanX =
                    this.pinchStartPanX;

                this.zoomPanY =
                    this.pinchStartPanY;

                this.applyMobileZoom(
                    this.zoomTarget
                );

                this.gesture =
                    'pinch';

                this.touchMoved = true;

                return;
            }

            if (
                event.touches.length !== 1
            ) {
                return;
            }

            const touch =
                event.touches[0];

            this.touchCurrentX =
                touch.clientX;

            this.touchCurrentY =
                touch.clientY;

            const deltaX =
                touch.clientX -
                this.touchStartX;

            const deltaY =
                touch.clientY -
                this.touchStartY;

            if (
                Math.abs(deltaX) < 8 &&
                Math.abs(deltaY) < 8
            ) {
                return;
            }

            this.touchMoved = true;

            /* Zoomed image → pan. */

            if (
                !this.supportsHoverZoom &&
                this.zoomed &&
                this.zoomTarget &&
                (
                    this.gesture ===
                    'possible-pan' ||
                    this.gesture === 'pan'
                )
            ) {
                this.gesture = 'pan';

                this.setZoomPan(
                    this.zoomTarget,
                    this.panStartTranslateX +
                    deltaX,
                    this.panStartTranslateY +
                    deltaY
                );

                this.applyMobileZoom(
                    this.zoomTarget
                );

                return;
            }

            /* Normal image → swipe or scroll. */

            if (
                this.gesture ===
                'possible-swipe'
            ) {
                if (
                    Math.abs(deltaY) >
                    Math.abs(deltaX)
                ) {
                    this.gesture =
                        'vertical-scroll';

                    return;
                }

                this.gesture = 'swipe';
            }
        }

        /* ==========================================================
           Touch end
           ========================================================== */

        handleTouchEnd(event) {
            /*
             * One finger remains after a pinch:
             * immediately transition into pan.
             */

            if (
                event.touches?.length === 1 &&
                this.gesture === 'pinch' &&
                this.zoomed &&
                this.zoomTarget
            ) {
                const touch =
                    event.touches[0];

                this.touchStartX =
                    touch.clientX;

                this.touchStartY =
                    touch.clientY;

                this.touchCurrentX =
                    touch.clientX;

                this.touchCurrentY =
                    touch.clientY;

                this.panStartTranslateX =
                    this.zoomPanX;

                this.panStartTranslateY =
                    this.zoomPanY;

                this.gesture =
                    'possible-pan';

                this.touchMoved = true;

                return;
            }

            if (
                event.touches?.length > 0 ||
                !this.touching
            ) {
                return;
            }

            const deltaX =
                this.touchCurrentX -
                this.touchStartX;

            const deltaY =
                this.touchCurrentY -
                this.touchStartY;

            const gesture =
                this.gesture;

            this.touching = false;

            if (gesture === 'pinch') {
                this.gesture = 'idle';

                if (
                    this.zoomScale <=
                    this.zoomMinScale
                ) {
                    this.resetZoom();
                }

                return;
            }

            /*
             * When zoomed, a drag normally pans.
             * At the horizontal edge it can become
             * a gallery swipe.
             */

            if (
                !this.supportsHoverZoom &&
                this.zoomed
            ) {
                if (gesture === 'pan') {
                    this.maybeSwipeWhileZoomed(
                        deltaX,
                        deltaY
                    );
                }

                this.gesture = 'idle';

                return;
            }

            if (
                gesture ===
                'vertical-scroll'
            ) {
                this.gesture = 'idle';

                return;
            }

            if (
                Math.abs(deltaX) < 50 ||
                Math.abs(deltaY) >
                Math.abs(deltaX)
            ) {
                this.gesture = 'idle';

                return;
            }

            this.touchMoved = true;
            this.gesture = 'idle';

            if (deltaX < 0) {
                this.next();
            } else {
                this.previous();
            }
        }

        /* ==========================================================
           Mobile zoom
           ========================================================== */

        toggleMobileZoom(target) {
            if (!target) {
                return;
            }

            if (
                this.zoomed &&
                this.zoomTarget === target
            ) {
                this.resetZoom();

                return;
            }

            this.activateMobileZoom(
                target
            );
        }

        activateMobileZoom(target) {
            const media =
                target?.closest(
                    '[data-gallery-media]'
                );

            if (
                !media ||
                media.dataset.mediaType !==
                'image'
            ) {
                return;
            }

            this.resetZoom();

            this.zoomTarget =
                target;

            this.zoomed = true;

            this.zoomScale = 1.75;

            this.zoomPanX = 0;
            this.zoomPanY = 0;

            target.classList.add(
                'is-zoomed'
            );

            target.setAttribute(
                'aria-expanded',
                'true'
            );

            this.setZoomScale(
                target,
                this.zoomScale
            );

            this.setZoomPan(
                target,
                0,
                0
            );

            this.applyMobileZoom(
                target
            );
        }

        /* ==========================================================
           Desktop zoom
           ========================================================== */

        activateDesktopZoom(
            target,
            event = null
        ) {
            const media =
                target?.closest(
                    '[data-gallery-media]'
                );

            if (
                !media ||
                media.dataset.mediaType !==
                'image'
            ) {
                return;
            }

            this.resetZoom();

            this.zoomTarget =
                target;

            this.zoomed = true;

            target.classList.add(
                'is-zoomed'
            );

            target.setAttribute(
                'aria-expanded',
                'true'
            );

            this.setZoomScale(
                target,
                this.desktopZoomScale
            );

            if (
                event &&
                typeof event.clientX ===
                'number'
            ) {
                this.updateZoomPosition(
                    target,
                    event
                );
            }
        }

        handlePointerMove(event) {
            if (
                !this.supportsHoverZoom ||
                !this.zoomed ||
                !this.zoomTarget
            ) {
                return;
            }

            const target =
                event.target.closest(
                    '[data-gallery-zoom]'
                );

            if (
                target ===
                this.zoomTarget
            ) {
                this.updateZoomPosition(
                    target,
                    event
                );
            }
        }

        handlePointerLeave() {
            /*
             * Desktop zoom intentionally stays
             * active until click or Escape.
             */
        }

        updateZoomPosition(
            target,
            event
        ) {
            const rect =
                target?.getBoundingClientRect();

            if (
                !rect?.width ||
                !rect?.height
            ) {
                return;
            }

            const x =
                (
                    (
                        event.clientX -
                        rect.left
                    ) /
                    rect.width
                ) *
                100;

            const y =
                (
                    (
                        event.clientY -
                        rect.top
                    ) /
                    rect.height
                ) *
                100;

            this.setZoomPosition(
                target,
                this.clamp(
                    x,
                    0,
                    100
                ),
                this.clamp(
                    y,
                    0,
                    100
                )
            );
        }

        setZoomPosition(
            target,
            x,
            y
        ) {
            if (!target) {
                return;
            }

            target.style.setProperty(
                '--zoom-x',
                `${x}%`
            );

            target.style.setProperty(
                '--zoom-y',
                `${y}%`
            );
        }

        setZoomScale(
            target,
            scale
        ) {
            if (!target) {
                return;
            }

            this.zoomScale =
                this.clamp(
                    scale,
                    this.zoomMinScale,
                    this.zoomMaxScale
                );

            target.style.setProperty(
                '--zoom-scale',
                String(
                    this.zoomScale
                )
            );
        }

        setZoomPan(
            target,
            x,
            y
        ) {
            if (!target) {
                return;
            }

            this.zoomPanX =
                Number.isFinite(x)
                    ? x
                    : 0;

            this.zoomPanY =
                Number.isFinite(y)
                    ? y
                    : 0;

            this.applyPanBounds(
                target
            );
        }

        applyPanBounds(target) {
            if (!target) {
                return;
            }

            if (
                this.zoomScale <= 1
            ) {
                this.zoomPanX = 0;
                this.zoomPanY = 0;

                return;
            }

            const rect =
                target.getBoundingClientRect();

            if (
                !rect.width ||
                !rect.height
            ) {
                return;
            }

            const maxX =
                (
                    rect.width *
                    (
                        this.zoomScale - 1
                    )
                ) /
                2;

            const maxY =
                (
                    rect.height *
                    (
                        this.zoomScale - 1
                    )
                ) /
                2;

            this.zoomPanX =
                this.clamp(
                    this.zoomPanX,
                    -maxX,
                    maxX
                );

            this.zoomPanY =
                this.clamp(
                    this.zoomPanY,
                    -maxY,
                    maxY
                );
        }

        maybeSwipeWhileZoomed(
            deltaX,
            deltaY
        ) {
            if (
                !this.zoomTarget ||
                Math.abs(deltaY) >
                Math.abs(deltaX)
            ) {
                return false;
            }

            const rect =
                this.zoomTarget
                    .getBoundingClientRect();

            if (!rect.width) {
                return false;
            }

            const maxX =
                (
                    rect.width *
                    (
                        this.zoomScale - 1
                    )
                ) /
                2;

            const requestedX =
                this.panStartTranslateX +
                deltaX;

            const overscroll =
                Math.abs(
                    requestedX
                ) -
                maxX;

            if (overscroll < 60) {
                return false;
            }

            this.touchMoved = true;

            if (deltaX < 0) {
                this.next();
            } else {
                this.previous();
            }

            return true;
        }

        applyMobileZoom(target) {
            if (!target) {
                return;
            }

            this.applyPanBounds(
                target
            );

            target.style.setProperty(
                '--zoom-scale',
                String(
                    this.zoomScale
                )
            );

            target.style.setProperty(
                '--zoom-pan-x',
                `${this.zoomPanX}px`
            );

            target.style.setProperty(
                '--zoom-pan-y',
                `${this.zoomPanY}px`
            );
        }

        resetZoom() {
            if (this.zoomTarget) {
                this.zoomTarget.classList.remove(
                    'is-zoomed'
                );

                this.zoomTarget.setAttribute(
                    'aria-expanded',
                    'false'
                );

                [
                    '--zoom-x',
                    '--zoom-y',
                    '--zoom-scale',
                    '--zoom-pan-x',
                    '--zoom-pan-y'
                ].forEach(
                    (property) => {
                        this.zoomTarget.style
                            .removeProperty(
                                property
                            );
                    }
                );
            }

            this.zoomTarget = null;

            this.zoomed = false;

            this.zoomScale = 1;

            this.zoomPanX = 0;
            this.zoomPanY = 0;
        }

        clamp(
            value,
            min,
            max
        ) {
            return Math.min(
                max,
                Math.max(
                    min,
                    value
                )
            );
        }

        /* ==========================================================
           Gallery navigation
           ========================================================== */

        previous() {
            if (
                this.mediaItems.length <= 1
            ) {
                return;
            }

            const index =
                this.activeIndex <= 0
                    ? this.mediaItems.length - 1
                    : this.activeIndex - 1;

            this.update(index);
        }

        next() {
            if (
                this.mediaItems.length <= 1
            ) {
                return;
            }

            const index =
                this.activeIndex >=
                    this.mediaItems.length - 1
                    ? 0
                    : this.activeIndex + 1;

            this.update(index);
        }

        goToMediaId(
            mediaId,
            options = {}
        ) {
            if (
                mediaId === null ||
                mediaId === undefined ||
                mediaId === ''
            ) {
                return false;
            }

            const index =
                this.mediaItems.findIndex(
                    (media) =>
                        String(
                            media.dataset.mediaId
                        ) ===
                        String(mediaId)
                );

            if (index < 0) {
                return false;
            }

            this.update(
                index,
                options
            );

            return true;
        }

        update(
            index,
            {
                initial = false,
                focus = false
            } = {}
        ) {
            if (
                !this.mediaItems.length
            ) {
                return;
            }

            const normalizedIndex =
                this.normalizeIndex(
                    index
                );

            if (
                normalizedIndex === null
            ) {
                return;
            }

            const previousIndex =
                this.activeIndex;

            this.resetZoom();

            this.activeIndex =
                normalizedIndex;

            this.mediaItems.forEach(
                (
                    media,
                    mediaIndex
                ) => {
                    const active =
                        mediaIndex ===
                        normalizedIndex;

                    media.classList.toggle(
                        'is-active',
                        active
                    );

                    media.hidden =
                        !active;

                    media.setAttribute(
                        'aria-hidden',
                        String(!active)
                    );

                    if (active) {
                        media.setAttribute(
                            'aria-current',
                            'true'
                        );
                    } else {
                        media.removeAttribute(
                            'aria-current'
                        );
                    }
                }
            );

            this.thumbnails.forEach(
                (
                    thumbnail,
                    thumbnailIndex
                ) => {
                    const active =
                        thumbnailIndex ===
                        normalizedIndex;

                    thumbnail.classList.toggle(
                        'is-active',
                        active
                    );

                    thumbnail.setAttribute(
                        'aria-current',
                        String(active)
                    );

                    thumbnail.setAttribute(
                        'aria-selected',
                        String(active)
                    );
                }
            );

            this.updateStatus();

            this.scrollThumbnailIntoView(
                normalizedIndex
            );

            this.manageMediaPlayback(
                previousIndex,
                normalizedIndex
            );

            if (focus) {
                this.thumbnails[
                    normalizedIndex
                ]?.focus({
                    preventScroll: true
                });
            }

            if (
                !initial &&
                previousIndex !==
                normalizedIndex
            ) {
                this.dispatchChange(
                    normalizedIndex
                );
            }
        }

        normalizeIndex(index) {
            if (
                !Number.isInteger(index) ||
                !this.mediaItems.length
            ) {
                return null;
            }

            if (index < 0) {
                return (
                    this.mediaItems.length - 1
                );
            }

            if (
                index >=
                this.mediaItems.length
            ) {
                return 0;
            }

            return index;
        }

        scrollThumbnailIntoView(
            index
        ) {
            const thumbnail =
                this.thumbnails[index];

            if (
                !thumbnail ||
                typeof thumbnail
                    .scrollIntoView !==
                'function'
            ) {
                return;
            }

            thumbnail.scrollIntoView({
                behavior:
                    this.isReducedMotion
                        ? 'auto'
                        : 'smooth',

                block: 'nearest',
                inline: 'nearest'
            });
        }

        updateStatus() {
            if (!this.status) {
                return;
            }

            this.status.textContent =
                this.mediaItems.length
                    ? `${this.activeIndex + 1} / ${this.mediaItems.length}`
                    : '';
        }

        manageMediaPlayback(
            previousIndex,
            activeIndex
        ) {
            if (
                previousIndex !==
                activeIndex
            ) {
                this.pauseMedia(
                    this.mediaItems[
                    previousIndex
                    ]
                );
            }
        }

        pauseMedia(media) {
            if (!media) {
                return;
            }

            media.querySelectorAll(
                'video'
            ).forEach(
                (video) => {
                    if (!video.paused) {
                        video.pause();
                    }
                }
            );
        }

        activateModel(model) {
            if (!model) {
                return;
            }

            if (
                typeof model.activateUI ===
                'function'
            ) {
                model.activateUI();

                return;
            }

            model.dispatchEvent(
                new CustomEvent(
                    'theme:model:activate',
                    {
                        bubbles: true,

                        detail: {
                            model
                        }
                    }
                )
            );
        }

        dispatchChange(index) {
            const media =
                this.mediaItems[index];

            document.dispatchEvent(
                new CustomEvent(
                    'theme:product:media-change',
                    {
                        bubbles: true,

                        detail: {
                            sectionId:
                                this.section.dataset
                                    .sectionId,

                            productId:
                                this.section.dataset
                                    .productId,

                            index,

                            media
                        }
                    }
                )
            );
        }

        destroy() {
            if (!this.gallery) {
                return;
            }

            this.resetZoom();

            this.gallery.removeEventListener(
                'click',
                this.handleClick
            );

            this.gallery.removeEventListener(
                'keydown',
                this.handleKeydown
            );

            this.gallery.removeEventListener(
                'touchstart',
                this.handleTouchStart,
                true
            );

            this.gallery.removeEventListener(
                'touchmove',
                this.handleTouchMove,
                true
            );

            this.gallery.removeEventListener(
                'touchend',
                this.handleTouchEnd,
                true
            );

            if (this.supportsHoverZoom) {
                this.gallery.removeEventListener(
                    'pointermove',
                    this.handlePointerMove
                );

                this.gallery.removeEventListener(
                    'pointerleave',
                    this.handlePointerLeave
                );
            }

            delete this.gallery.dataset
                .initialized;

            this.gallery = null;
            this.thumbnails = [];
            this.mediaItems = [];
            this.status = null;
            this.section = null;
        }
    }

    /* ==========================================================
       Variant Picker
  
       Uses:
       product_option_value.id
         ↓
       ?option_values=id,id
         ↓
       Shopify Section Rendering API
         ↓
       authoritative server-rendered state
       ========================================================== */

    class VariantPicker {
        constructor(section) {
            this.section =
                section;

            this.container =
                section.querySelector(
                    '[data-product-variant-picker]'
                );

            if (!this.container) {
                return;
            }

            this.optionGroups = [
                ...this.container.querySelectorAll(
                    '[data-product-option]'
                )
            ];

            this.sectionId =
                section.dataset.sectionId ||
                '';

            this.productUrl =
                this.container.dataset
                    .productUrl ||
                section.dataset.productUrl ||
                window.location.pathname;

            this.requestController =
                null;

            this.requestSequence =
                0;

            this.handleChange =
                this.handleChange.bind(this);
        }

        /* ==========================================================
           Init
           ========================================================== */

        init() {
            if (!this.container) {
                return;
            }

            this.container.addEventListener(
                'change',
                this.handleChange
            );

            this.syncSelectedLabels();
        }

        /* ==========================================================
           Selection helpers
           ========================================================== */

        getSelectedInputs() {
            return this.optionGroups
                .map(
                    (group) =>
                        group.querySelector(
                            '[data-option-input]:checked'
                        )
                )
                .filter(Boolean);
        }

        getSelectedOptionValueIds() {
            return this.getSelectedInputs()
                .map(
                    (input) =>
                        input.dataset
                            .optionValueId
                )
                .filter(Boolean);
        }

        syncSelectedLabels() {
            this.optionGroups.forEach(
                (group) => {
                    const checked =
                        group.querySelector(
                            '[data-option-input]:checked'
                        );

                    const display =
                        group.querySelector(
                            '[data-option-selected-value]'
                        );

                    if (!display) {
                        return;
                    }

                    display.textContent =
                        checked
                            ? checked.value
                            : '';
                }
            );
        }

        /* ==========================================================
           Change
           ========================================================== */

        handleChange(event) {
            const input =
                event.target.closest(
                    '[data-option-input]'
                );

            if (
                !input ||
                !this.container?.contains(
                    input
                )
            ) {
                return;
            }

            this.syncSelectedLabels();

            const selectedIds =
                this.getSelectedOptionValueIds();

            if (!selectedIds.length) {
                return;
            }

            this.renderSelection(
                input,
                selectedIds
            );
        }

        /* ==========================================================
           Section Rendering API
           ========================================================== */

        async renderSelection(
            input,
            selectedIds
        ) {
            const targetProductUrl =
                input.dataset.productUrl ||
                this.productUrl ||
                window.location.pathname;

            /*
             * Normal Shopify JSON sections
             * always have an ID. Fall back to
             * navigation if this component is
             * ever rendered outside one.
             */

            if (!this.sectionId) {
                this.navigateFallback(
                    targetProductUrl,
                    selectedIds
                );

                return;
            }

            /*
             * Rapid clicks shouldn't let an
             * older response overwrite a newer
             * option selection.
             */

            if (
                this.requestController
            ) {
                this.requestController.abort();
            }

            const controller =
                new AbortController();

            this.requestController =
                controller;

            const sequence =
                ++this.requestSequence;

            const requestUrl =
                this.buildSelectionUrl(
                    targetProductUrl,
                    selectedIds,
                    {
                        sectionRequest: true
                    }
                );

            const focusOptionValueId =
                input.dataset.optionValueId ||
                '';

            this.setBusy(true);

            document.dispatchEvent(
                new CustomEvent(
                    'theme:product:option-change',
                    {
                        bubbles: true,

                        detail: {
                            sectionId:
                                this.sectionId,

                            productId:
                                this.section.dataset
                                    .productId,

                            optionValueIds:
                                selectedIds,

                            productUrl:
                                targetProductUrl
                        }
                    }
                )
            );

            try {
                const response =
                    await fetch(
                        requestUrl.toString(),
                        {
                            method: 'GET',

                            headers: {
                                Accept:
                                    'text/html',

                                'X-Requested-With':
                                    'XMLHttpRequest'
                            },

                            credentials:
                                'same-origin',

                            signal:
                                controller.signal
                        }
                    );

                if (!response.ok) {
                    throw new Error(
                        `Unable to render product section (${response.status}).`
                    );
                }

                const responseText =
                    await response.text();

                /*
                 * Ignore an old response if a
                 * newer selection has already
                 * started.
                 */

                if (
                    sequence !==
                    this.requestSequence
                ) {
                    return;
                }

                const parsed =
                    new DOMParser()
                        .parseFromString(
                            responseText,
                            'text/html'
                        );

                const newRenderRoot =
                    parsed.querySelector(
                        '[data-product-render-root]'
                    );

                const newSection =
                    newRenderRoot
                        ?.querySelector(
                            '.product[data-section-id]'
                        ) ||
                    parsed.querySelector(
                        '.product[data-section-id]'
                    );

                if (
                    !newRenderRoot ||
                    !newSection
                ) {
                    throw new Error(
                        'Shopify returned product HTML without the expected product root.'
                    );
                }

                /*
                 * The response has already been
                 * consumed. Clear the controller
                 * before destroying the old
                 * product controller.
                 */

                this.requestController =
                    null;

                const productChanged =
                    this.didProductChange(
                        targetProductUrl,
                        newSection
                    );

                this.applyRenderedSection(
                    newRenderRoot,
                    newSection,
                    {
                        productChanged,
                        focusOptionValueId
                    }
                );

                this.updateBrowserUrl(
                    targetProductUrl,
                    selectedIds,
                    newSection
                );

                document.dispatchEvent(
                    new CustomEvent(
                        'theme:product:variant-rendered',
                        {
                            bubbles: true,

                            detail: {
                                sectionId:
                                    this.sectionId,

                                productId:
                                    newSection.dataset
                                        .productId,

                                variantId:
                                    newSection.dataset
                                        .selectedVariantId ||
                                    null,

                                productChanged
                            }
                        }
                    )
                );
            } catch (error) {
                if (
                    error?.name ===
                    'AbortError'
                ) {
                    return;
                }

                console.error(
                    '[Main Product] Variant section rendering failed.',
                    error
                );

                /*
                 * Graceful fallback:
                 * only perform a normal page load
                 * if the AJAX request itself fails.
                 */

                this.navigateFallback(
                    targetProductUrl,
                    selectedIds
                );
            } finally {
                /*
                 * Don't remove the busy state from
                 * a newer request when an older
                 * request was aborted.
                 */

                if (
                    this.requestController ===
                    controller
                ) {
                    this.requestController =
                        null;

                    this.setBusy(false);
                }
            }
        }

        /* ==========================================================
           URL construction
           ========================================================== */

        buildSelectionUrl(
            targetProductUrl,
            selectedIds,
            {
                sectionRequest = false
            } = {}
        ) {
            const url =
                new URL(
                    targetProductUrl ||
                    window.location.pathname,
                    window.location.origin
                );

            const currentUrl =
                new URL(
                    window.location.href
                );

            /*
             * Preserve useful storefront state:
             * selling plans, Markets/preview
             * query data and campaign values.
             *
             * Variant-specific query state is
             * rebuilt below.
             */

            currentUrl.searchParams.forEach(
                (
                    value,
                    key
                ) => {
                    if (
                        key === 'variant' ||
                        key === 'option_values' ||
                        key === 'section_id' ||
                        key === 'sections'
                    ) {
                        return;
                    }

                    if (
                        !url.searchParams.has(key)
                    ) {
                        url.searchParams.append(
                            key,
                            value
                        );
                    }
                }
            );

            url.searchParams.delete(
                'variant'
            );

            url.searchParams.delete(
                'option_values'
            );

            url.searchParams.delete(
                'section_id'
            );

            url.searchParams.delete(
                'sections'
            );

            if (selectedIds.length) {
                url.searchParams.set(
                    'option_values',
                    selectedIds.join(',')
                );
            }

            if (sectionRequest) {
                url.searchParams.set(
                    'section_id',
                    this.sectionId
                );
            }

            return url;
        }

        /* ==========================================================
           Combined Listing detection
           ========================================================== */

        didProductChange(
            targetProductUrl,
            newSection
        ) {
            const oldProductId =
                String(
                    this.section.dataset
                        .productId ||
                    ''
                );

            const newProductId =
                String(
                    newSection.dataset
                        .productId ||
                    ''
                );

            if (
                oldProductId &&
                newProductId &&
                oldProductId !==
                newProductId
            ) {
                return true;
            }

            const currentUrl =
                new URL(
                    this.productUrl ||
                    window.location.pathname,
                    window.location.origin
                );

            const targetUrl =
                new URL(
                    targetProductUrl ||
                    window.location.pathname,
                    window.location.origin
                );

            return (
                currentUrl.pathname !==
                targetUrl.pathname
            );
        }

        /* ==========================================================
           Apply server-rendered state
           ========================================================== */

        applyRenderedSection(
            newRenderRoot,
            newSection,
            {
                productChanged = false,
                focusOptionValueId = ''
            } = {}
        ) {
            const currentSection =
                this.section;

            const currentInstance =
                instances.get(
                    this.sectionId
                );

            /*
             * Destroy listeners before replacing
             * their DOM nodes.
             */

            if (currentInstance) {
                currentInstance.destroy();

                instances.delete(
                    this.sectionId
                );
            }

            /*
             * Combined Listing:
             *
             * A child product can have different
             * title, description, gallery,
             * product ID and app-block content.
             * Replace the complete product
             * render root.
             */

            if (productChanged) {
                const currentRenderRoot =
                    currentSection.closest(
                        '[data-product-render-root]'
                    );

                if (!currentRenderRoot) {
                    throw new Error(
                        'Unable to locate the current product render root.'
                    );
                }

                currentRenderRoot.replaceWith(
                    newRenderRoot
                );

                const insertedSection =
                    newRenderRoot.querySelector(
                        '.product[data-section-id]'
                    );

                if (insertedSection) {
                    initializeSection(
                        insertedSection
                    );

                    this.restoreFocus(
                        insertedSection,
                        focusOptionValueId
                    );
                }

                return;
            }

            /*
             * Same product:
             *
             * Keep app blocks, description,
             * merchant disclosures and popup
             * state mounted.
             *
             * Only replace data that changes
             * with the selected variant.
             */

            const selectors = [
                '[data-product-gallery]',
                '[data-product-block-type="price"]',
                '[data-product-block-type="variant_picker"]',
                '[data-product-block-type="sku"]',
                '[data-product-block-type="pickup_availability"]',
                '[data-product-block-type="buy_buttons"]'
            ];

            selectors.forEach(
                (selector) => {
                    const currentElement =
                        currentSection.querySelector(
                            selector
                        );

                    const newElement =
                        newSection.querySelector(
                            selector
                        );

                    if (
                        currentElement &&
                        newElement
                    ) {
                        currentElement.replaceWith(
                            newElement
                        );

                        return;
                    }

                    if (
                        currentElement &&
                        !newElement
                    ) {
                        currentElement.remove();
                    }
                }
            );

            /*
             * Mirror server-rendered product
             * metadata onto the live section.
             */

            currentSection.dataset.productId =
                newSection.dataset.productId ||
                currentSection.dataset.productId ||
                '';

            currentSection.dataset.productHandle =
                newSection.dataset.productHandle ||
                currentSection.dataset.productHandle ||
                '';

            currentSection.dataset.productUrl =
                newSection.dataset.productUrl ||
                currentSection.dataset.productUrl ||
                '';

            if (
                newSection.dataset
                    .selectedVariantId
            ) {
                currentSection.dataset
                    .selectedVariantId =
                    newSection.dataset
                        .selectedVariantId;
            } else {
                delete currentSection.dataset
                    .selectedVariantId;
            }

            /*
             * Rebind gallery, quantity,
             * Add to Cart and popup controllers
             * to the newly inserted DOM.
             */

            initializeSection(
                currentSection
            );

            this.restoreFocus(
                currentSection,
                focusOptionValueId
            );
        }

        /* ==========================================================
           Focus restoration
           ========================================================== */

        restoreFocus(
            section,
            optionValueId
        ) {
            if (
                !section ||
                !optionValueId
            ) {
                return;
            }

            const input = [
                ...section.querySelectorAll(
                    '[data-option-input][data-option-value-id]'
                )
            ].find(
                (candidate) =>
                    candidate.dataset
                        .optionValueId ===
                    optionValueId
            );

            if (!input) {
                return;
            }

            requestAnimationFrame(
                () => {
                    input.focus({
                        preventScroll: true
                    });
                }
            );
        }

        /* ==========================================================
           Browser URL
           ========================================================== */

        updateBrowserUrl(
            targetProductUrl,
            selectedIds,
            newSection
        ) {
            const url =
                this.buildSelectionUrl(
                    targetProductUrl,
                    selectedIds
                );

            const variantId =
                newSection.dataset
                    .selectedVariantId;

            /*
             * A valid completed selection gets
             * Shopify's familiar ?variant= URL.
             *
             * Invalid/partial option state keeps
             * ?option_values= so Shopify can
             * reconstruct that exact state.
             */

            if (variantId) {
                url.searchParams.delete(
                    'option_values'
                );

                url.searchParams.set(
                    'variant',
                    variantId
                );
            }

            window.history.replaceState(
                {
                    ...(
                        window.history.state ||
                        {}
                    ),

                    themeProductVariant:
                        true
                },
                '',
                `${url.pathname}${url.search}${url.hash}`
            );
        }

        /* ==========================================================
           Busy state
           ========================================================== */

        setBusy(isBusy) {
            if (!this.section) {
                return;
            }

            if (isBusy) {
                this.section.setAttribute(
                    'aria-busy',
                    'true'
                );
            } else {
                this.section.removeAttribute(
                    'aria-busy'
                );
            }
        }

        /* ==========================================================
           Fallback
           ========================================================== */

        navigateFallback(
            targetProductUrl,
            selectedIds
        ) {
            const url =
                this.buildSelectionUrl(
                    targetProductUrl,
                    selectedIds
                );

            window.location.assign(
                url.toString()
            );
        }

        /* ==========================================================
           Destroy
           ========================================================== */

        destroy() {
            if (this.container) {
                this.container.removeEventListener(
                    'change',
                    this.handleChange
                );
            }

            if (
                this.requestController
            ) {
                this.requestController.abort();
            }

            this.requestController =
                null;

            this.section = null;
            this.container = null;

            this.optionGroups = [];
        }
    }

    /* ==========================================================
       Quantity Controller
       ========================================================== */

    class QuantityController {
        constructor(section) {
            this.section =
                section;

            this.input =
                section.querySelector(
                    '[data-quantity-input]'
                );

            if (!this.input) {
                return;
            }

            this.stepper =
                section.querySelector(
                    '.product__quantity-stepper'
                );

            this.decrementButton =
                section.querySelector(
                    '[data-quantity-decrement]'
                );

            this.incrementButton =
                section.querySelector(
                    '[data-quantity-increment]'
                );

            this.message =
                section.querySelector(
                    '[data-quantity-message]'
                );

            this.handleInputChange =
                this.handleInputChange.bind(this);

            this.handleDecrement =
                this.handleDecrement.bind(this);

            this.handleIncrement =
                this.handleIncrement.bind(this);
        }

        init() {
            if (!this.input) {
                return;
            }

            this.input.addEventListener(
                'change',
                this.handleInputChange
            );

            this.input.addEventListener(
                'blur',
                this.handleInputChange
            );

            if (
                this.decrementButton
            ) {
                this.decrementButton
                    .addEventListener(
                        'click',
                        this.handleDecrement
                    );
            }

            if (
                this.incrementButton
            ) {
                this.incrementButton
                    .addEventListener(
                        'click',
                        this.handleIncrement
                    );
            }

            this.updateStepperButtonStates();
        }

        handleDecrement() {
            this.step(-1);
        }

        handleIncrement() {
            this.step(1);
        }

        step(direction) {
            if (!this.input) {
                return;
            }

            const step =
                Number(
                    this.input.step
                ) ||
                1;

            const current =
                Number(
                    this.input.value
                ) ||
                0;

            this.input.value =
                String(
                    current +
                    direction * step
                );

            this.clampAndValidate();
        }

        updateStepperButtonStates() {
            if (!this.input) {
                return;
            }

            const value =
                Number(
                    this.input.value
                );

            const min =
                Number(
                    this.input.min
                );

            const maxAttribute =
                this.input.getAttribute(
                    'max'
                );

            const max =
                maxAttribute !== null &&
                    maxAttribute !== ''
                    ? Number(maxAttribute)
                    : null;

            if (
                this.decrementButton
            ) {
                this.decrementButton.disabled =
                    Number.isFinite(min) &&
                    value <= min;
            }

            if (
                this.incrementButton
            ) {
                this.incrementButton.disabled =
                    Number.isFinite(max) &&
                    value >= max;
            }
        }

        handleInputChange() {
            this.clampAndValidate();
        }

        clampAndValidate(
            {
                silent = false
            } = {}
        ) {
            if (!this.input) {
                return true;
            }

            const min =
                Number(
                    this.input.min
                ) ||
                1;

            const maxAttribute =
                this.input.getAttribute(
                    'max'
                );

            const max =
                maxAttribute !== null &&
                    maxAttribute !== ''
                    ? Number(maxAttribute)
                    : null;

            const step =
                Number(
                    this.input.step
                ) ||
                1;

            const raw =
                Number(
                    this.input.value
                );

            let value =
                Number.isFinite(raw)
                    ? raw
                    : min;

            let message =
                null;

            if (value < min) {
                value = min;

                message =
                    `Minimum quantity is ${min}.`;
            } else if (
                Number.isFinite(max) &&
                value > max
            ) {
                value = max;

                message =
                    `Only ${max} available.`;
            }

            /*
             * Snap to a valid Shopify
             * quantity-rule increment.
             */

            const steps =
                Math.round(
                    (
                        value -
                        min
                    ) /
                    step
                );

            const snapped =
                min +
                steps * step;

            if (
                snapped !== value &&
                snapped >= min &&
                (
                    !Number.isFinite(max) ||
                    snapped <= max
                )
            ) {
                value = snapped;
            }

            if (
                String(value) !==
                this.input.value
            ) {
                this.input.value =
                    String(value);
            }

            this.updateStepperButtonStates();

            const invalid =
                Boolean(message);

            if (this.stepper) {
                this.stepper.classList.toggle(
                    'is-invalid',
                    invalid
                );
            } else {
                this.input.classList.toggle(
                    'is-invalid',
                    invalid
                );
            }

            if (
                !silent &&
                this.message
            ) {
                this.message.textContent =
                    message ||
                    '';

                this.message.classList.toggle(
                    'is-warning',
                    invalid
                );
            }

            return !invalid;
        }

        destroy() {
            if (this.input) {
                this.input.removeEventListener(
                    'change',
                    this.handleInputChange
                );

                this.input.removeEventListener(
                    'blur',
                    this.handleInputChange
                );
            }

            if (
                this.decrementButton
            ) {
                this.decrementButton
                    .removeEventListener(
                        'click',
                        this.handleDecrement
                    );
            }

            if (
                this.incrementButton
            ) {
                this.incrementButton
                    .removeEventListener(
                        'click',
                        this.handleIncrement
                    );
            }

            this.section = null;

            this.input = null;

            this.stepper = null;

            this.decrementButton = null;

            this.incrementButton = null;

            this.message = null;
        }
    }

    /* ==========================================================
       Add To Cart Form
       ========================================================== */

    class AddToCartForm {
        constructor(
            section,
            {
                beforeSubmit = null
            } = {}
        ) {
            this.section =
                section;

            this.beforeSubmit =
                typeof beforeSubmit ===
                    'function'
                    ? beforeSubmit
                    : null;

            /*
             * IMPORTANT:
             *
             * Only bind the theme's product
             * form. App blocks are allowed to
             * contain forms of their own.
             */

            this.form =
                section.querySelector(
                    '[data-product-form]'
                );

            if (!this.form) {
                return;
            }

            this.variantIdInput =
                this.form.querySelector(
                    '[data-product-variant-id]'
                );

            this.button =
                this.form.querySelector(
                    '[data-add-to-cart-button]'
                );

            this.feedback =
                this.form.querySelector(
                    '[data-cart-feedback]'
                );

            this.submitting =
                false;

            this.feedbackTimeout =
                null;

            /*
             * Locale/Markets-safe Shopify
             * AJAX routes.
             */

            const root =
                window.Shopify
                    ?.routes
                    ?.root ||
                '/';

            const routeRoot =
                root.endsWith('/')
                    ? root
                    : `${root}/`;

            this.cartAddUrl =
                `${routeRoot}cart/add.js`;

            this.cartJsonUrl =
                `${routeRoot}cart.js`;

            this.handleSubmit =
                this.handleSubmit.bind(this);
        }

        init() {
            if (!this.form) {
                return;
            }

            this.form.addEventListener(
                'submit',
                this.handleSubmit
            );
        }

        /* ==========================================================
           Submit
           ========================================================== */

        async handleSubmit(event) {
            event.preventDefault();

            if (this.submitting) {
                return;
            }

            if (
                this.beforeSubmit &&
                this.beforeSubmit() === false
            ) {
                return;
            }

            if (
                !this.variantIdInput?.value
            ) {
                this.showFeedback(
                    strings.unavailable,
                    'error'
                );

                return;
            }

            this.submitting = true;

            this.setLoading(true);

            this.clearFeedback();

            try {
                const response =
                    await fetch(
                        this.cartAddUrl,
                        {
                            method: 'POST',

                            headers: {
                                Accept:
                                    'application/json'
                            },

                            body:
                                new FormData(
                                    this.form
                                )
                        }
                    );

                const data =
                    await response.json();

                if (!response.ok) {
                    throw data;
                }

                this.handleSuccess(
                    data
                );
            } catch (error) {
                this.handleError(
                    error
                );
            } finally {
                this.submitting = false;

                this.setLoading(false);
            }
        }

        /* ==========================================================
           Success
           ========================================================== */

        handleSuccess(item) {
            this.showFeedback(
                strings.addedToCart,
                'success'
            );

            /*
             * PDP Add to Cart intentionally
             * dispatches the neutral theme
             * cart:add event without a
             * quick-add source marker, allowing
             * the cart drawer to open.
             */

            dispatchDocumentEvent(
                'theme:cart:add',
                {
                    sectionId:
                        this.section.dataset
                            .sectionId,

                    productId:
                        this.section.dataset
                            .productId,

                    item
                },
                'flash:cart:add'
            );

            this.refreshCart();
        }

        /* ==========================================================
           Cart refresh
           ========================================================== */

        async refreshCart() {
            try {
                const response =
                    await fetch(
                        this.cartJsonUrl,
                        {
                            headers: {
                                Accept:
                                    'application/json'
                            }
                        }
                    );

                if (!response.ok) {
                    return;
                }

                const cart =
                    await response.json();

                dispatchDocumentEvent(
                    'theme:cart:update',
                    {
                        cart
                    },
                    'flash:cart:update'
                );

                this.updateCartCountElements(
                    cart
                );
            } catch (_) {
                /*
                 * The cart add already succeeded.
                 * Don't turn a secondary refresh
                 * failure into a false cart error.
                 */
            }
        }

        updateCartCountElements(cart) {
            if (
                !cart ||
                typeof cart.item_count !==
                'number'
            ) {
                return;
            }

            document
                .querySelectorAll(
                    '[data-cart-count]'
                )
                .forEach(
                    (element) => {
                        element.textContent =
                            String(
                                cart.item_count
                            );
                    }
                );
        }

        /* ==========================================================
           Error
           ========================================================== */

        handleError(error) {
            const message =
                error?.description ||
                error?.message ||
                strings.cartError;

            this.showFeedback(
                message,
                'error'
            );
        }

        /* ==========================================================
           Loading
           ========================================================== */

        setLoading(isLoading) {
            if (!this.button) {
                return;
            }

            this.button.disabled =
                isLoading;

            this.button.classList.toggle(
                'is-loading',
                isLoading
            );
        }

        /* ==========================================================
           Feedback
           ========================================================== */

        showFeedback(
            message,
            type
        ) {
            if (!this.feedback) {
                return;
            }

            if (
                this.feedbackTimeout
            ) {
                clearTimeout(
                    this.feedbackTimeout
                );
            }

            this.feedbackTimeout =
                null;

            this.feedback.textContent =
                message;

            this.feedback.classList.remove(
                'is-success',
                'is-error'
            );

            this.feedback.classList.add(
                type === 'error'
                    ? 'is-error'
                    : 'is-success'
            );

            this.feedback.classList.add(
                'is-visible'
            );

            if (type !== 'error') {
                this.feedbackTimeout =
                    window.setTimeout(
                        () => {
                            this.clearFeedback();
                        },
                        4000
                    );
            }
        }

        clearFeedback() {
            if (!this.feedback) {
                return;
            }

            if (
                this.feedbackTimeout
            ) {
                clearTimeout(
                    this.feedbackTimeout
                );
            }

            this.feedbackTimeout =
                null;

            this.feedback.textContent =
                '';

            this.feedback.classList.remove(
                'is-visible',
                'is-success',
                'is-error'
            );
        }

        destroy() {
            if (this.form) {
                this.form.removeEventListener(
                    'submit',
                    this.handleSubmit
                );
            }

            if (
                this.feedbackTimeout
            ) {
                clearTimeout(
                    this.feedbackTimeout
                );
            }

            this.form = null;

            this.variantIdInput = null;

            this.button = null;

            this.feedback = null;

            this.section = null;
        }
    }

    /* ==========================================================
       Stock Indicator
       ========================================================== */

    class StockIndicator {
        constructor(section) {
            this.section =
                section;

            this.element =
                section.querySelector(
                    '[data-stock-indicator]'
                );

            this.label =
                section.querySelector(
                    '[data-stock-label]'
                );
        }

        /*
         * Primarily a defensive fallback.
         * Normal variant state is now
         * rendered by Shopify.
         */

        showUnavailable() {
            if (!this.element) {
                return;
            }

            this.element.classList.remove(
                'product__stock-indicator--in-stock',
                'product__stock-indicator--low-stock',
                'product__stock-indicator--out-of-stock'
            );

            this.element.classList.add(
                'product__stock-indicator--out-of-stock'
            );

            if (this.label) {
                this.label.textContent =
                    'Unavailable';
            }
        }

        destroy() {
            this.section = null;

            this.element = null;

            this.label = null;
        }
    }

    /* ==========================================================
       Popup Blocks
       ========================================================== */

    class PopupBlocks {
        constructor(section) {
            this.section =
                section;

            this.triggers = [
                ...section.querySelectorAll(
                    '[data-popup-trigger]'
                )
            ];

            this.closeButtons = [
                ...section.querySelectorAll(
                    '[data-popup-close]'
                )
            ];

            this.dialogs = [
                ...section.querySelectorAll(
                    '[data-popup]'
                )
            ];

            this.handleTriggerClick =
                this.handleTriggerClick.bind(this);

            this.handleCloseClick =
                this.handleCloseClick.bind(this);

            this.handleDialogClick =
                this.handleDialogClick.bind(this);

            this.handleDialogClose =
                this.handleDialogClose.bind(this);
        }

        init() {
            this.triggers.forEach(
                (trigger) => {
                    trigger.addEventListener(
                        'click',
                        this.handleTriggerClick
                    );
                }
            );

            this.closeButtons.forEach(
                (button) => {
                    button.addEventListener(
                        'click',
                        this.handleCloseClick
                    );
                }
            );

            this.dialogs.forEach(
                (dialog) => {
                    dialog.addEventListener(
                        'click',
                        this.handleDialogClick
                    );

                    dialog.addEventListener(
                        'close',
                        this.handleDialogClose
                    );
                }
            );
        }

        handleTriggerClick(event) {
            const id =
                event.currentTarget
                    .dataset
                    .popupTrigger;

            const dialog =
                id
                    ? document.getElementById(
                        id
                    )
                    : null;

            if (
                !dialog ||
                typeof dialog.showModal !==
                'function'
            ) {
                return;
            }

            dialog.showModal();

            requestAnimationFrame(
                () => {
                    dialog.classList.add(
                        'is-open'
                    );
                }
            );
        }

        handleCloseClick(event) {
            const dialog =
                event.currentTarget
                    .closest(
                        'dialog'
                    );

            this.closeDialog(
                dialog
            );
        }

        handleDialogClick(event) {
            if (
                event.target ===
                event.currentTarget
            ) {
                this.closeDialog(
                    event.currentTarget
                );
            }
        }

        handleDialogClose(event) {
            event.currentTarget
                .classList.remove(
                    'is-open'
                );
        }

        closeDialog(dialog) {
            if (
                !dialog?.open
            ) {
                return;
            }

            dialog.classList.remove(
                'is-open'
            );

            if (
                window.matchMedia(
                    '(prefers-reduced-motion: reduce)'
                ).matches
            ) {
                dialog.close();

                return;
            }

            let finished = false;

            const finish =
                () => {
                    if (finished) {
                        return;
                    }

                    finished = true;

                    dialog.removeEventListener(
                        'transitionend',
                        finish
                    );

                    if (dialog.open) {
                        dialog.close();
                    }
                };

            dialog.addEventListener(
                'transitionend',
                finish
            );

            window.setTimeout(
                finish,
                250
            );
        }

        destroy() {
            this.triggers.forEach(
                (trigger) => {
                    trigger.removeEventListener(
                        'click',
                        this.handleTriggerClick
                    );
                }
            );

            this.closeButtons.forEach(
                (button) => {
                    button.removeEventListener(
                        'click',
                        this.handleCloseClick
                    );
                }
            );

            this.dialogs.forEach(
                (dialog) => {
                    dialog.removeEventListener(
                        'click',
                        this.handleDialogClick
                    );

                    dialog.removeEventListener(
                        'close',
                        this.handleDialogClose
                    );
                }
            );

            this.triggers = [];

            this.closeButtons = [];

            this.dialogs = [];

            this.section = null;
        }
    }

    /* ==========================================================
       Main Product
       ========================================================== */

    class MainProduct {
        constructor(section) {
            this.section =
                section;

            this.gallery =
                new ProductGallery(
                    section
                );

            this.stockIndicator =
                new StockIndicator(
                    section
                );

            this.popupBlocks =
                new PopupBlocks(
                    section
                );

            this.variantPicker =
                new VariantPicker(
                    section
                );

            this.quantityController =
                new QuantityController(
                    section
                );

            this.addToCartForm =
                new AddToCartForm(
                    section,
                    {
                        beforeSubmit:
                            () =>
                                this.quantityController
                                    ?.clampAndValidate() !==
                                false
                    }
                );
        }

        init() {
            this.gallery?.init();

            this.variantPicker?.init();

            this.quantityController?.init();

            this.addToCartForm?.init();

            this.popupBlocks?.init();
        }

        destroy() {
            this.popupBlocks?.destroy();

            this.addToCartForm?.destroy();

            this.quantityController
                ?.destroy();

            this.variantPicker?.destroy();

            this.stockIndicator?.destroy();

            this.gallery?.destroy();

            this.gallery = null;

            this.variantPicker = null;

            this.quantityController = null;

            this.addToCartForm = null;

            this.stockIndicator = null;

            this.popupBlocks = null;

            this.section = null;
        }
    }

    /* ==========================================================
       Instances
       ========================================================== */

    const instances =
        new Map();

    const initializeSection =
        (section) => {
            if (!section) {
                return;
            }

            const sectionId =
                section.dataset.sectionId;

            if (!sectionId) {
                return;
            }

            const existing =
                instances.get(
                    sectionId
                );

            if (existing) {
                existing.destroy();

                instances.delete(
                    sectionId
                );
            }

            const product =
                new MainProduct(
                    section
                );

            product.init();

            instances.set(
                sectionId,
                product
            );
        };

    const destroySection =
        (sectionId) => {
            if (!sectionId) {
                return;
            }

            const instance =
                instances.get(
                    sectionId
                );

            if (!instance) {
                return;
            }

            instance.destroy();

            instances.delete(
                sectionId
            );
        };

    const initializeAll =
        () => {
            document
                .querySelectorAll(
                    '.product[data-section-id]'
                )
                .forEach(
                    initializeSection
                );
        };

    /* ==========================================================
       Initial load
       ========================================================== */

    const init =
        () => {
            initializeAll();
        };

    if (
        document.readyState ===
        'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            init,
            {
                once: true
            }
        );
    } else {
        init();
    }

    /* ==========================================================
       Shopify Theme Editor
       ========================================================== */

    document.addEventListener(
        'shopify:section:load',
        (event) => {
            const section =
                event.target.closest?.(
                    '.product[data-section-id]'
                ) ||
                event.target.querySelector?.(
                    '.product[data-section-id]'
                );

            if (!section) {
                return;
            }

            initializeSection(
                section
            );
        }
    );

    document.addEventListener(
        'shopify:section:unload',
        (event) => {
            destroySection(
                event.detail?.sectionId
            );
        }
    );

    document.addEventListener(
        'shopify:section:select',
        (event) => {
            const section =
                event.target.closest?.(
                    '.product[data-section-id]'
                );

            if (!section) {
                return;
            }

            const sectionId =
                section.dataset.sectionId;

            if (
                !instances.has(
                    sectionId
                )
            ) {
                initializeSection(
                    section
                );
            }
        }
    );
})();